import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * AlertExplainDialog — click "Explain" on any AlertCard → this pops with a
 * grounded GPT explanation of the specific alert. Backend proxies the call
 * so the OpenAI key never touches the browser. Falls back cleanly when the
 * copilot is disabled (503 → "add OPENAI_API_KEY to backend/.env" message).
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { MockPill } from "../common/MockPill";
import { getBaseUrl } from "../../lib/api";
const backdrop = {
    position: "fixed",
    inset: 0,
    background: "rgba(0, 13, 16, 0.72)",
    zIndex: 3100,
    display: "grid",
    placeItems: "center",
    padding: 20,
    backdropFilter: "blur(3px)",
};
const modal = {
    width: "min(560px, 100%)",
    background: "var(--color-pure-white)",
    borderRadius: 6,
    overflow: "hidden",
    boxShadow: "0 20px 40px rgba(0,0,0,0.32)",
    display: "flex",
    flexDirection: "column",
};
const header = {
    padding: "14px 18px",
    borderBottom: "1px solid var(--color-pebble)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
};
const body = {
    padding: "18px 20px",
    fontSize: 15,
    lineHeight: 1.55,
    minHeight: 120,
    color: "var(--color-deep-ink, #000d10)",
};
const footer = {
    padding: "10px 18px",
    borderTop: "1px solid var(--color-pebble)",
    fontSize: 11,
    color: "var(--color-cool-ash)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
};
const closeBtn = {
    padding: "6px 12px",
    background: "transparent",
    border: "1px solid var(--color-pebble)",
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    cursor: "pointer",
};
export function AlertExplainDialog({ alert, onClose }) {
    const [text, setText] = useState("");
    const [error, setError] = useState(null);
    const [model, setModel] = useState(null);
    useEffect(() => {
        let cancelled = false;
        setText("");
        setError(null);
        fetch(`${getBaseUrl()}/api/copilot/explain-alert`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                alert: {
                    id: alert.id,
                    headline: alert.headline,
                    pair: alert.vehicles,
                    forecast_gap_s: alert.forecast_gap_s,
                    forecast_lead_s: alert.forecast_lead_s,
                    suggested_hold_bus: alert.suggested_hold_bus,
                    suggested_hold_stop: alert.suggested_hold_stop,
                    suggested_hold_s: alert.suggested_hold_s,
                    display_state: alert.displayState,
                },
                related_vehicles: [],
            }),
        })
            .then(async (r) => {
            if (r.status === 503) {
                const j = await r.json().catch(() => ({}));
                throw new Error(j.detail ?? "Copilot disabled");
            }
            if (!r.ok)
                throw new Error(`HTTP ${r.status}`);
            return r.json();
        })
            .then((j) => {
            if (cancelled)
                return;
            setText(j.text);
            setModel(j.model);
        })
            .catch((e) => {
            if (!cancelled)
                setError(String(e.message ?? e));
        });
        return () => {
            cancelled = true;
        };
    }, [alert.id]);
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape")
                onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);
    return createPortal(_jsxs("div", { style: backdrop, onClick: (e) => {
            if (e.target === e.currentTarget)
                onClose();
        }, "data-testid": "alert-explain-dialog", children: [_jsxs("div", { style: modal, children: [_jsxs("div", { style: header, children: [_jsxs("div", { children: [_jsxs("div", { style: {
                                            fontSize: 10,
                                            fontWeight: 700,
                                            letterSpacing: "0.16em",
                                            textTransform: "uppercase",
                                            color: "var(--color-cool-ash)",
                                            marginBottom: 3,
                                            display: "flex",
                                            gap: 8,
                                            alignItems: "center",
                                        }, children: [_jsx("span", { children: "AI Explanation" }), _jsx(MockPill, { kind: "live", label: "GPT \u00B7 GROUNDED" })] }), _jsx("div", { style: { fontSize: 16, fontWeight: 700 }, children: alert.headline })] }), _jsx("button", { type: "button", style: closeBtn, onClick: onClose, "aria-label": "Close", children: "\u2715" })] }), _jsx("div", { style: body, "aria-live": "polite", children: error ? (_jsxs("div", { style: { color: "#a25c46" }, children: [_jsx("div", { style: { fontWeight: 700, marginBottom: 6 }, children: "Copilot unavailable" }), _jsxs("div", { style: { fontSize: 13 }, children: [error, " \u2014 set ", _jsx("code", { children: "OPENAI_API_KEY" }), " in", " ", _jsx("code", { children: "backend/.env" }), " and restart."] })] })) : text ? (_jsx("p", { style: { margin: 0 }, children: text })) : (_jsxs("div", { style: { display: "flex", gap: 8, alignItems: "center" }, children: [_jsx("span", { "aria-hidden": true, style: {
                                        width: 10,
                                        height: 10,
                                        borderRadius: "50%",
                                        background: "var(--color-clay-ember, #bc7155)",
                                        animation: "bb-copilot-pulse 1.1s ease-in-out infinite",
                                    } }), _jsx("span", { style: { color: "var(--color-cool-ash)" }, children: "Grounding on live alert JSON\u2026" })] })) }), _jsxs("div", { style: footer, children: [_jsx("span", { children: model
                                    ? `model: ${model} · grounded on alert JSON`
                                    : "grounded on live alert JSON" }), _jsx("span", { children: "Press ESC to close" })] })] }), _jsx("style", { children: `
        @keyframes bb-copilot-pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.3); opacity: 0.45; }
        }
      ` })] }), document.body);
}
